import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

// Desktop and mobile navigation are both mounted. Coalesce their requests
// until React commits the open state, so two toggles cannot close the report.
const pendingOpen = new WeakSet();
function expandOnce(button) {
  if (button?.getAttribute('aria-expanded') !== 'false' || pendingOpen.has(button)) return;
  pendingOpen.add(button);
  button.click();
  requestAnimationFrame(() => pendingOpen.delete(button));
}

export default function ReportLinks({ onNavigate, accordion = false, hideProgramNumbers = false, catalog, path }) {
  const location = useLocation();
  const [reports, setReports] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  useEffect(() => { setExpandedGroups({}); }, [location.pathname, location.search]);
  useEffect(() => {
    const main = document.getElementById('main-content');
    if (!main) return undefined;
    const update = () => {
      const next = [...main.querySelectorAll('[data-report-title]')].map(node => ({ id: node.id, title: node.dataset.reportTitle, parent: node.closest('[data-report-parent]')?.dataset.reportParent }));
      setReports(previous => JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(main, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-report-title'] });
    return () => observer.disconnect();
  }, [location.pathname, location.search]);

  const openReport = id => {
    const heading = document.getElementById(id);
    if (!heading) return;
    const parent = heading.closest('[data-report-parent]')?.dataset.reportParent;
    const parentButton = parent && document.getElementById(parent)?.querySelector('button[aria-expanded]');
    expandOnce(parentButton);
    const button = heading.querySelector('button[aria-expanded]');
    expandOnce(button);
    onNavigate?.();
    requestAnimationFrame(() => {
      heading.scrollIntoView?.({ block: 'start', behavior: 'auto' });
      button?.focus({ preventScroll: true });
    });
  };
  useEffect(() => {
    const id = location.hash.slice(1);
    if (reports.some(report => report.id === id)) openReport(id);
    // Resolve direct links after asynchronous reports load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports, location.hash]);
  const menuReports = catalog ? [...catalog, ...reports.filter(report => !catalog.some(item => item.id === report.id))] : reports;
  if (!menuReports.length) return null;
  const renderLink = report => <Link
      to={`${path || location.pathname}${path && path !== location.pathname ? '' : location.search}#${report.id}`}
      className={`${accordion ? "px-4 py-3 font-medium hover:bg-blue-900" : "rounded px-2 py-2 hover:bg-blue-800"} block text-sm text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white`}
      onClick={event => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        if (path && path !== location.pathname) { onNavigate?.(); return; }
        event.preventDefault();
        const currentSearch = window.location.pathname === location.pathname ? window.location.search : location.search;
        window.history.replaceState(window.history.state, '', `${location.pathname}${currentSearch}#${report.id}`);
        openReport(report.id);
      }}
    >{hideProgramNumbers ? report.title.replace(/^\d{4}\s*[-–—]\s*/, '') : report.title}</Link>;
  return <ul aria-label="Reports in this section" className={accordion ? "ml-3 overflow-hidden rounded-md border border-blue-800 bg-blue-950" : "ml-3 space-y-1 border-l border-blue-400 pl-2"}>
    {menuReports.filter(report=>!report.parent).map(report => <li key={report.id} className={accordion ? "border-b border-blue-900 last:border-b-0" : undefined}>
      {menuReports.some(child=>child.parent===report.id)?<>
        <button type="button" aria-expanded={!!expandedGroups[report.id]}
          onClick={()=>setExpandedGroups(groups=>({...groups,[report.id]:!groups[report.id]}))}
          className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium text-white hover:bg-blue-900">
          {report.title}<span aria-hidden="true">{expandedGroups[report.id]?'▾':'▸'}</span>
        </button>
        {expandedGroups[report.id]&&<ul className="ml-3 border-l border-blue-400">{menuReports.filter(child=>child.parent===report.id).map(child=><li key={child.id}>{renderLink(child)}</li>)}</ul>}
      </>:renderLink(report)}
    </li>)}
  </ul>;
}
